/**
 * Mojo's Class - Wuff Simplified Theme System
 *
 * Design Philosophy: "Less is more"
 * - Gradient used sparingly (Streak card, active nav, brand buttons)
 * - Teal CTA buttons
 * - Clean single-shadow cards
 * - Warm, inviting neutrals
 */

export interface Theme {
  id: string;
  name: string;
  description: string;

  // Base colors
  background: string;      // Main page background
  backgroundAlt: string;   // Section dividers, muted fills
  surface: string;         // Cards, modals (white)

  // Brand gradient (used sparingly)
  gradientStart: string;
  gradientEnd: string;

  // CTA button color (teal by default)
  cta: string;
  ctaDark: string;
  ctaLight: string;

  // Accent color (for highlights, stars)
  amber: string;
  amberLight: string;
  amberDark: string;

  // Functional colors
  success: string;
  successLight: string;
  error: string;
  errorLight: string;

  // Text colors
  text: string;
  textMuted: string;
  textLight: string;
  textOnBrand: string;

  // Border & shadow
  border: string;
  shadow: string;
  shadowMd: string;

  // Sidebar (trainer)
  sidebarBg: string;
  sidebarText: string;
  sidebarTextMuted: string;
  sidebarActive: string;
  sidebarActiveBg: string;

  // Legacy compatibility properties
  primary: string;
  primaryLight: string;
  primaryDark: string;
  secondary: string;
  secondaryLight: string;
  accent: string;
  accentLight: string;
  warning: string;
  warningLight: string;
  info: string;
  infoLight: string;
  textOnPrimary: string;
  surfaceHover: string;
  surfacePressed: string;
  borderLight: string;

  // Chart colors
  chart: {
    primary: string;
    secondary: string;
    tertiary: string;
    quaternary: string;
    quinary: string;
    gradientStart: string;
    gradientEnd: string;
  };
}

export const themes: Record<string, Theme> = {
  // ═══════════════════════════════════════════════════════════════
  // MORGENROTE - Warm Amber/Coral (Default)
  // ═══════════════════════════════════════════════════════════════
  morgenroete: {
    id: 'morgenroete',
    name: 'Morgenrote',
    description: 'Warm und einladend mit Amber und Coral',

    background: '#FAFAF8',
    backgroundAlt: '#F4F1ED',
    surface: '#FFFFFF',

    gradientStart: '#F59E0B',
    gradientEnd: '#F97316',

    cta: '#0D9488',
    ctaDark: '#0F766E',
    ctaLight: '#CCFBF1',

    amber: '#F59E0B',
    amberLight: '#FEF3C7',
    amberDark: '#B45309',

    success: '#10B981',
    successLight: '#D1FAE5',
    error: '#EF4444',
    errorLight: '#FEE2E2',

    text: '#1C1917',
    textMuted: '#78716C',
    textLight: '#A8A29E',
    textOnBrand: '#FFFFFF',

    border: '#EDE9E4',
    shadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.05)',
    shadowMd: '0 2px 8px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.06)',

    sidebarBg: '#1C1917',
    sidebarText: 'rgba(255,255,255,0.8)',
    sidebarTextMuted: 'rgba(255,255,255,0.45)',
    sidebarActive: '#F59E0B',
    sidebarActiveBg: 'rgba(245,158,11,0.15)',

    // Legacy compatibility
    primary: '#F59E0B',
    primaryLight: '#FEF3C7',
    primaryDark: '#B45309',
    secondary: '#0D9488',
    secondaryLight: '#CCFBF1',
    accent: '#F97316',
    accentLight: '#FFF0E6',
    warning: '#F59E0B',
    warningLight: '#FEF3C7',
    info: '#0EA5E9',
    infoLight: '#E0F2FE',
    textOnPrimary: '#FFFFFF',
    surfaceHover: '#FAFAF8',
    surfacePressed: '#F4F1ED',
    borderLight: '#F4F1ED',
    chart: {
      primary: '#F59E0B',
      secondary: '#0D9488',
      tertiary: '#F97316',
      quaternary: '#10B981',
      quinary: '#0EA5E9',
      gradientStart: '#F59E0B',
      gradientEnd: '#FEF3C7',
    },
  },

  // ═══════════════════════════════════════════════════════════════
  // WALDWEG - Forest Green
  // ═══════════════════════════════════════════════════════════════
  waldweg: {
    id: 'waldweg',
    name: 'Waldweg',
    description: 'Natürlich und geerdet mit Waldgrün',

    background: '#FAFAF8',
    backgroundAlt: '#F0F4F1',
    surface: '#FFFFFF',

    gradientStart: '#4A7C59',
    gradientEnd: '#52B788',

    cta: '#16A34A',
    ctaDark: '#15803D',
    ctaLight: '#DCFCE7',

    amber: '#4A7C59',
    amberLight: '#D1FAE5',
    amberDark: '#365840',

    success: '#10B981',
    successLight: '#D1FAE5',
    error: '#EF4444',
    errorLight: '#FEE2E2',

    text: '#1C1917',
    textMuted: '#5A6B62',
    textLight: '#9EAAA3',
    textOnBrand: '#FFFFFF',

    border: '#E0E5DE',
    shadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.05)',
    shadowMd: '0 2px 8px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.06)',

    sidebarBg: '#1C2820',
    sidebarText: 'rgba(255,255,255,0.8)',
    sidebarTextMuted: 'rgba(255,255,255,0.45)',
    sidebarActive: '#52B788',
    sidebarActiveBg: 'rgba(82,183,136,0.15)',

    // Legacy compatibility
    primary: '#4A7C59',
    primaryLight: '#D1FAE5',
    primaryDark: '#365840',
    secondary: '#16A34A',
    secondaryLight: '#DCFCE7',
    accent: '#52B788',
    accentLight: '#E5F0E8',
    warning: '#F59E0B',
    warningLight: '#FEF3C7',
    info: '#0EA5E9',
    infoLight: '#E0F2FE',
    textOnPrimary: '#FFFFFF',
    surfaceHover: '#FAFAF8',
    surfacePressed: '#F0F4F1',
    borderLight: '#F0F4F1',
    chart: {
      primary: '#4A7C59',
      secondary: '#16A34A',
      tertiary: '#52B788',
      quaternary: '#10B981',
      quinary: '#0EA5E9',
      gradientStart: '#4A7C59',
      gradientEnd: '#D1FAE5',
    },
  },

  // ═══════════════════════════════════════════════════════════════
  // OZEAN - Ocean Blue/Cyan
  // ═══════════════════════════════════════════════════════════════
  ozean: {
    id: 'ozean',
    name: 'Ozean',
    description: 'Kühl und klar mit Meeresblau',

    background: '#FAFAFA',
    backgroundAlt: '#F0F5F8',
    surface: '#FFFFFF',

    gradientStart: '#0284C7',
    gradientEnd: '#06B6D4',

    cta: '#0369A1',
    ctaDark: '#1D4ED8',
    ctaLight: '#E0F2FE',

    amber: '#0284C7',
    amberLight: '#E0F2FE',
    amberDark: '#075985',

    success: '#10B981',
    successLight: '#D1FAE5',
    error: '#EF4444',
    errorLight: '#FEE2E2',

    text: '#1A2B3C',
    textMuted: '#5A6C7D',
    textLight: '#A0B0C0',
    textOnBrand: '#FFFFFF',

    border: '#E0EBF2',
    shadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.05)',
    shadowMd: '0 2px 8px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.06)',

    sidebarBg: '#0F172A',
    sidebarText: 'rgba(255,255,255,0.8)',
    sidebarTextMuted: 'rgba(255,255,255,0.45)',
    sidebarActive: '#06B6D4',
    sidebarActiveBg: 'rgba(6,182,212,0.15)',

    // Legacy compatibility
    primary: '#0284C7',
    primaryLight: '#E0F2FE',
    primaryDark: '#075985',
    secondary: '#06B6D4',
    secondaryLight: '#CFFAFE',
    accent: '#06B6D4',
    accentLight: '#E0F7FA',
    warning: '#F59E0B',
    warningLight: '#FEF3C7',
    info: '#0EA5E9',
    infoLight: '#E0F2FE',
    textOnPrimary: '#FFFFFF',
    surfaceHover: '#FAFAFA',
    surfacePressed: '#F0F5F8',
    borderLight: '#F0F5F8',
    chart: {
      primary: '#0284C7',
      secondary: '#06B6D4',
      tertiary: '#0EA5E9',
      quaternary: '#10B981',
      quinary: '#F59E0B',
      gradientStart: '#0284C7',
      gradientEnd: '#E0F2FE',
    },
  },

  // ═══════════════════════════════════════════════════════════════
  // LAVENDEL - Purple/Violet
  // ═══════════════════════════════════════════════════════════════
  lavendel: {
    id: 'lavendel',
    name: 'Lavendel',
    description: 'Sanft und verspielt mit Lavendel',

    background: '#FAFAF9',
    backgroundAlt: '#F5F3F8',
    surface: '#FFFFFF',

    gradientStart: '#7C3AED',
    gradientEnd: '#A855F7',

    cta: '#6D28D9',
    ctaDark: '#5B21B6',
    ctaLight: '#EDE9FE',

    amber: '#7C3AED',
    amberLight: '#EDE9FE',
    amberDark: '#5B21B6',

    success: '#10B981',
    successLight: '#D1FAE5',
    error: '#EF4444',
    errorLight: '#FEE2E2',

    text: '#2D2A3E',
    textMuted: '#6B6880',
    textLight: '#A8A5B8',
    textOnBrand: '#FFFFFF',

    border: '#E8E5F0',
    shadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.05)',
    shadowMd: '0 2px 8px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.06)',

    sidebarBg: '#1E1B2E',
    sidebarText: 'rgba(255,255,255,0.8)',
    sidebarTextMuted: 'rgba(255,255,255,0.45)',
    sidebarActive: '#A855F7',
    sidebarActiveBg: 'rgba(168,85,247,0.15)',

    // Legacy compatibility
    primary: '#7C3AED',
    primaryLight: '#EDE9FE',
    primaryDark: '#5B21B6',
    secondary: '#A855F7',
    secondaryLight: '#F3E8FF',
    accent: '#A855F7',
    accentLight: '#F5F3FF',
    warning: '#F59E0B',
    warningLight: '#FEF3C7',
    info: '#0EA5E9',
    infoLight: '#E0F2FE',
    textOnPrimary: '#FFFFFF',
    surfaceHover: '#FAFAF9',
    surfacePressed: '#F5F3F8',
    borderLight: '#F5F3F8',
    chart: {
      primary: '#7C3AED',
      secondary: '#A855F7',
      tertiary: '#6366F1',
      quaternary: '#10B981',
      quinary: '#F59E0B',
      gradientStart: '#7C3AED',
      gradientEnd: '#EDE9FE',
    },
  },

  // ═══════════════════════════════════════════════════════════════
  // KIRSCHE - Cherry Red/Pink
  // ═══════════════════════════════════════════════════════════════
  kirsche: {
    id: 'kirsche',
    name: 'Kirsche',
    description: 'Lebendig und energetisch mit Kirschrot',

    background: '#FAFAF9',
    backgroundAlt: '#FDF2F4',
    surface: '#FFFFFF',

    gradientStart: '#E11D48',
    gradientEnd: '#F43F5E',

    cta: '#BE123C',
    ctaDark: '#9F1239',
    ctaLight: '#FFE4E6',

    amber: '#E11D48',
    amberLight: '#FFE4E6',
    amberDark: '#9F1239',

    success: '#10B981',
    successLight: '#D1FAE5',
    error: '#EF4444',
    errorLight: '#FEE2E2',

    text: '#1C1917',
    textMuted: '#78716C',
    textLight: '#A8A29E',
    textOnBrand: '#FFFFFF',

    border: '#F0E6E8',
    shadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.05)',
    shadowMd: '0 2px 8px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.06)',

    sidebarBg: '#1C1517',
    sidebarText: 'rgba(255,255,255,0.8)',
    sidebarTextMuted: 'rgba(255,255,255,0.45)',
    sidebarActive: '#F43F5E',
    sidebarActiveBg: 'rgba(244,63,94,0.15)',

    // Legacy compatibility
    primary: '#E11D48',
    primaryLight: '#FFE4E6',
    primaryDark: '#9F1239',
    secondary: '#F43F5E',
    secondaryLight: '#FFE4E6',
    accent: '#F43F5E',
    accentLight: '#FFF0F3',
    warning: '#F59E0B',
    warningLight: '#FEF3C7',
    info: '#0EA5E9',
    infoLight: '#E0F2FE',
    textOnPrimary: '#FFFFFF',
    surfaceHover: '#FAFAF9',
    surfacePressed: '#FDF2F4',
    borderLight: '#FDF2F4',
    chart: {
      primary: '#E11D48',
      secondary: '#F43F5E',
      tertiary: '#FB7185',
      quaternary: '#10B981',
      quinary: '#F59E0B',
      gradientStart: '#E11D48',
      gradientEnd: '#FFE4E6',
    },
  },

  // ═══════════════════════════════════════════════════════════════
  // MITTERNACHT - Dark Mode
  // ═══════════════════════════════════════════════════════════════
  mitternacht: {
    id: 'mitternacht',
    name: 'Mitternacht',
    description: 'Augenschonend im Dunkelmodus',

    background: '#1E293B',
    backgroundAlt: '#334155',
    surface: '#1E293B',

    gradientStart: '#6366F1',
    gradientEnd: '#8B5CF6',

    cta: '#6366F1',
    ctaDark: '#4F46E5',
    ctaLight: '#312E81',

    amber: '#FBBF24',
    amberLight: '#3A3520',
    amberDark: '#D97706',

    success: '#10B981',
    successLight: '#064E3B',
    error: '#EF4444',
    errorLight: '#7F1D1D',

    text: '#F1F5F9',
    textMuted: '#94A3B8',
    textLight: '#64748B',
    textOnBrand: '#FFFFFF',

    border: '#475569',
    shadow: '0 1px 3px rgba(0,0,0,0.3), 0 4px 12px rgba(0,0,0,0.25)',
    shadowMd: '0 2px 8px rgba(0,0,0,0.35), 0 8px 24px rgba(0,0,0,0.3)',

    sidebarBg: '#0F172A',
    sidebarText: 'rgba(255,255,255,0.9)',
    sidebarTextMuted: 'rgba(255,255,255,0.5)',
    sidebarActive: '#818CF8',
    sidebarActiveBg: 'rgba(129,140,248,0.2)',

    // Legacy compatibility
    primary: '#6366F1',
    primaryLight: '#312E81',
    primaryDark: '#4F46E5',
    secondary: '#8B5CF6',
    secondaryLight: '#3730A3',
    accent: '#8B5CF6',
    accentLight: '#2E1065',
    warning: '#FBBF24',
    warningLight: '#3A3520',
    info: '#0EA5E9',
    infoLight: '#0C4A6E',
    textOnPrimary: '#FFFFFF',
    surfaceHover: '#334155',
    surfacePressed: '#475569',
    borderLight: '#334155',
    chart: {
      primary: '#6366F1',
      secondary: '#8B5CF6',
      tertiary: '#A78BFA',
      quaternary: '#10B981',
      quinary: '#FBBF24',
      gradientStart: '#6366F1',
      gradientEnd: '#312E81',
    },
  },
};

// Default theme
export const defaultTheme = themes.morgenroete;

// Get theme by ID with fallback
export function getTheme(themeId: string): Theme {
  return themes[themeId] || defaultTheme;
}

// Get all theme options for selector
export function getThemeOptions(): Array<{ id: string; name: string; description: string }> {
  return Object.values(themes).map(({ id, name, description }) => ({
    id,
    name,
    description,
  }));
}
